/**
 * ApiKeyManager — Admin component for managing the Gemini API Key
 * Stored in Supabase (ai_config table), with ENV fallback.
 */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { authFetch } from "../../../services/authFetch";

const ADMIN_COLOR = "#a3e635";

export const ApiKeyManager: React.FC = () => {
  const { t } = useTranslation();
  const [maskedKey, setMaskedKey] = useState("");
  const [source, setSource] = useState("");
  const [hasKey, setHasKey] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [showInput, setShowInput] = useState(false);

  const loadStatus = async () => {
    try {
      const res = await authFetch("/api/admin/llm-config");
      const data = await res.json();
      setMaskedKey(data.masked || "");
      setSource(data.source || "env");
      setHasKey(data.hasKey || false);
    } catch (e) {
      setMaskedKey(t("admin.apiKeyManager.loadError"));
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleTest = async () => {
    if (!newKey.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await authFetch("/api/admin/llm-config/test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ apiKey: newKey.trim() }),
      });
      const data = await res.json();
      setTestResult({
        success: data.success,
        message: data.success
          ? `✅ ${t("admin.apiKeyManager.keyValid")}`
          : `❌ ${data.error || t("admin.apiKeyManager.keyInvalid")}`,
      });
    } catch (e: any) {
      setTestResult({
        success: false,
        message: `❌ ${t("admin.apiKeyManager.errorPrefix", { message: e.message })}`,
      });
    }
    setTesting(false);
  };

  const handleSave = async () => {
    if (!newKey.trim() || !testResult?.success) return;
    setSaving(true);
    try {
      const res = await authFetch("/api/admin/llm-config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ apiKey: newKey.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setMaskedKey(data.masked);
        setSource("supabase");
        setHasKey(true);
        setNewKey("");
        setShowInput(false);
        setTestResult(null);
      }
    } catch (e: any) {
      setTestResult({
        success: false,
        message: `❌ ${t("admin.apiKeyManager.saveError", { message: e.message })}`,
      });
    }
    setSaving(false);
  };

  return (
    <div
      style={{
        background: "rgba(0,0,0,0.15)",
        border: "1px solid rgba(255,255,255,0.05)",
        borderRadius: "10px",
        padding: "14px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <i className="bx bx-key" style={{ fontSize: "1.2rem", color: "#F59E0B" }}></i>
          <span style={{ fontWeight: 900, letterSpacing: "1px", fontSize: "0.8rem" }}>
            {t("admin.apiKeyManager.geminiApiKey")}
          </span>
        </div>
        <span
          style={{
            fontSize: "0.6rem",
            fontWeight: 800,
            padding: "3px 10px",
            borderRadius: "6px",
            background: source === "supabase" ? "rgba(16,185,129,0.15)" : "rgba(233,30,139,0.15)",
            color: source === "supabase" ? "#10B981" : "#e91e8b",
            letterSpacing: "0.5px",
          }}
        >
          {source === "supabase" ? t("admin.apiKeyManager.supabase") : t("admin.apiKeyManager.env")}
        </span>
      </div>

      {/* Current key display */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          background: "rgba(0,0,0,0.2)",
          borderRadius: "8px",
          padding: "12px 16px",
          marginBottom: "12px",
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: "0.6rem",
              color: "var(--color-text-muted)",
              fontWeight: 800,
              marginBottom: "4px",
              letterSpacing: "0.5px",
            }}
          >
            {t("admin.apiKeyManager.keyActive")}
          </div>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "0.9rem",
              color: hasKey ? "#fff" : "#EF4444",
              fontWeight: 700,
            }}
          >
            {maskedKey || "—"}
          </div>
        </div>
        <button
          onClick={() => {
            setShowInput(!showInput);
            setTestResult(null);
            setNewKey("");
          }}
          style={{
            background: showInput ? "rgba(239,68,68,0.15)" : `rgba(139, 92, 246, 0.15)`,
            border: `1px solid ${showInput ? "rgba(239,68,68,0.4)" : ADMIN_COLOR}`,
            color: showInput ? "#EF4444" : ADMIN_COLOR,
            padding: "8px 16px",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "0.7rem",
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            gap: "6px",
            transition: "all 0.2s",
          }}
        >
          <i
            className={`bx ${showInput ? "bx-x" : "bx-edit-alt"}`}
            style={{ fontSize: "0.9rem" }}
          ></i>
          {showInput ? t("admin.apiKeyManager.cancel") : t("admin.apiKeyManager.change")}
        </button>
      </div>

      {/* New key input */}
      {showInput && (
        <div
          style={{
            background: "rgba(233,30,139,0.05)",
            border: `1px solid rgba(233,30,139,0.2)`,
            borderRadius: "10px",
            padding: "14px",
            animation: "fadeIn 0.2s ease",
          }}
        >
          <div
            style={{ fontSize: "0.65rem", color: "#ccc", marginBottom: "10px", fontWeight: 700 }}
          >
            {t("admin.apiKeyManager.pasteGeminiKey")}
          </div>
          <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
            <input
              type="password"
              placeholder={t("admin.apiKeyManager.geminiPlaceholder")}
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              style={{
                flex: 1,
                background: "rgba(0,0,0,0.3)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#fff",
                borderRadius: "8px",
                padding: "10px 14px",
                fontSize: "0.8rem",
                fontFamily: "var(--font-mono)",
                outline: "none",
              }}
            />
            <button
              onClick={handleTest}
              disabled={!newKey.trim() || testing}
              style={{
                background: testing ? "rgba(255,255,255,0.05)" : "rgba(245,158,11,0.15)",
                border: `1px solid ${testing ? "rgba(255,255,255,0.1)" : "rgba(245,158,11,0.4)"}`,
                color: testing ? "#888" : "#F59E0B",
                padding: "10px 18px",
                borderRadius: "8px",
                cursor: testing ? "wait" : "pointer",
                fontSize: "0.7rem",
                fontWeight: 800,
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <i className={`bx ${testing ? "bx-loader-alt bx-spin" : "bx-check-circle"}`}></i>
              {testing ? t("admin.apiKeyManager.testing") : t("admin.apiKeyManager.test")}
            </button>
          </div>

          {testResult && (
            <div
              style={{
                fontSize: "0.75rem",
                padding: "8px 12px",
                borderRadius: "6px",
                marginBottom: "10px",
                background: testResult.success ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
                color: testResult.success ? "#10B981" : "#EF4444",
                fontWeight: 700,
              }}
            >
              {testResult.message}
            </div>
          )}

          {testResult?.success && (
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                background: saving ? "rgba(255,255,255,0.05)" : "rgba(16,185,129,0.15)",
                border: "1px solid rgba(16,185,129,0.4)",
                color: "#10B981",
                padding: "10px 24px",
                borderRadius: "8px",
                cursor: saving ? "wait" : "pointer",
                fontSize: "0.75rem",
                fontWeight: 800,
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <i className={`bx ${saving ? "bx-loader-alt bx-spin" : "bx-save"}`}></i>
              {saving ? t("admin.apiKeyManager.saving") : t("admin.apiKeyManager.saveAndActivate")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
